import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Search,
  Plane,
  Trash2,
  Edit,
  Download,
  RefreshCw,
  RotateCcw,
  Check,
  X
} from "lucide-react";

const SettingsAerolineas = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [airlines, setAirlines] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAirline, setEditingAirline] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [newAirline, setNewAirline] = useState({
    code: "",
    name: "",
    country: "",
    is_domestic_mexico: false
  });

  const handleEdit = async () => {
    if (!editingAirline) return;
    try {
      await axios.put(`${API}/airlines/${editingAirline.id}`, {
        code: editingAirline.code,
        name: editingAirline.name,
        country: editingAirline.country,
        is_domestic_mexico: editingAirline.is_domestic_mexico
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aerolínea actualizada");
      setShowEditDialog(false);
      setEditingAirline(null);
      fetchAirlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar aerolínea");
    }
  };

  useEffect(() => {
    fetchAirlines();
  }, [showInactive]);

  const fetchAirlines = async () => {
    try {
      // Always fetch all airlines and filter in frontend based on showInactive
      const res = await axios.get(`${API}/airlines/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter based on showInactive: true = only inactive, false = only active
      const filtered = showInactive 
        ? res.data.filter(a => a.is_active === false)
        : res.data.filter(a => a.is_active !== false);
      setAirlines(filtered);
    } catch (error) {
      console.error("Error fetching airlines:", error);
      toast.error("Error al cargar aerolíneas");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndImport = async () => {
    if (!confirm("¿Estás seguro? Esto eliminará TODAS las aerolíneas y las volverá a importar.")) {
      return;
    }
    setImporting(true);
    try {
      await axios.delete(`${API}/airlines/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Aerolíneas eliminadas, importando...");
      
      const res = await axios.post(`${API}/airlines/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAirlines();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Error al reimportar aerolíneas");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/airlines/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchAirlines();
    } catch (error) {
      console.error("Error importing airlines:", error);
      toast.error(error.response?.data?.detail || "Error al importar aerolíneas");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!newAirline.code || !newAirline.name || !newAirline.country) {
      toast.error("Código, nombre y país son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/airlines`, newAirline, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aerolínea agregada");
      setShowAddDialog(false);
      setNewAirline({ code: "", name: "", country: "", is_domestic_mexico: false });
      fetchAirlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar aerolínea");
    }
  };

  const handleDelete = async (airlineId) => {
    if (!confirm(`¿Inactivar esta aerolínea?`)) return;
    try {
      await axios.delete(`${API}/airlines/${airlineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aerolínea inactivada");
      fetchAirlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inactivar aerolínea");
    }
  };

  const handleRestore = async (airlineId) => {
    try {
      await axios.post(`${API}/airlines/restore/${airlineId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Aerolínea restaurada");
      fetchAirlines();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar aerolínea");
    }
  };

  const filteredAirlines = airlines.filter(airline => {
    const search = searchTerm.toLowerCase();
    return (
      airline.code?.toLowerCase().includes(search) ||
      airline.name?.toLowerCase().includes(search) ||
      airline.country?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando aerolíneas...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-aerolineas-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Aerolíneas
              </h1>
              <p className="text-slate-500">Catálogo de aerolíneas para cotizaciones de vuelos</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importing}
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Importar Nuevas"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearAndImport}
              disabled={importing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Limpiar y Reimportar"}
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Stats and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${showInactive ? 'bg-red-50' : 'bg-blue-50'} px-4 py-2 rounded-lg`}>
                  <Plane className={`w-5 h-5 ${showInactive ? 'text-red-600' : 'text-blue-600'}`} />
                  <span className={`font-semibold ${showInactive ? 'text-red-900' : 'text-blue-900'}`}>{airlines.length}</span>
                  <span className={showInactive ? 'text-red-600' : 'text-blue-600'}>
                    {showInactive ? 'aerolíneas inactivas' : 'aerolíneas activas'}
                  </span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded"
                  />
                  Mostrar solo inactivas
                </label>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por código, nombre, país..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="airline-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Airlines Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plane className="w-5 h-5 text-slate-600" />
              Catálogo de Aerolíneas
            </CardTitle>
            <CardDescription>
              Incluye aerolíneas mexicanas domésticas y las principales internacionales
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead className="w-[100px]">Código</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead className="w-[120px]">Tipo</TableHead>
                    <TableHead className="w-[100px]">Estado</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAirlines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                        {airlines.length === 0 
                          ? "No hay aerolíneas. Haz clic en 'Importar Nuevas' para cargar el catálogo."
                          : "No se encontraron aerolíneas con ese criterio de búsqueda"
                        }
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAirlines.slice(0, 200).map((airline) => (
                      <TableRow 
                        key={airline.id}
                        className={airline.is_active === false ? "opacity-50 bg-slate-50" : ""}
                      >
                        <TableCell className="font-mono font-bold text-blue-600">
                          {airline.code}
                        </TableCell>
                        <TableCell className="font-medium">{airline.name}</TableCell>
                        <TableCell>{airline.country}</TableCell>
                        <TableCell>
                          {airline.is_domestic_mexico ? (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                              México
                            </span>
                          ) : (
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                              Internacional
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {airline.is_active === false ? (
                            <span className="flex items-center gap-1 text-xs text-red-600">
                              <X className="w-3 h-3" /> Inactivo
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Check className="w-3 h-3" /> Activo
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingAirline({...airline});
                                setShowEditDialog(true);
                              }}
                              className="h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {airline.is_active === false ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRestore(airline.id)}
                                className="h-8 w-8 text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(airline.id)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {filteredAirlines.length > 200 && (
              <p className="text-sm text-slate-500 mt-2 text-center">
                Mostrando 200 de {filteredAirlines.length} aerolíneas. Usa el buscador para filtrar.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Aerolínea</DialogTitle>
              <DialogDescription>
                Ingresa los datos de la nueva aerolínea
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Código IATA *</Label>
                <Input
                  value={newAirline.code}
                  onChange={(e) => setNewAirline({...newAirline, code: e.target.value.toUpperCase()})}
                  placeholder="AM"
                  maxLength={3}
                />
              </div>
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={newAirline.name}
                  onChange={(e) => setNewAirline({...newAirline, name: e.target.value})}
                  placeholder="Aeroméxico"
                />
              </div>
              <div className="space-y-2">
                <Label>País *</Label>
                <Input
                  value={newAirline.country}
                  onChange={(e) => setNewAirline({...newAirline, country: e.target.value})}
                  placeholder="México"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_domestic"
                  checked={newAirline.is_domestic_mexico}
                  onChange={(e) => setNewAirline({...newAirline, is_domestic_mexico: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="is_domestic" className="cursor-pointer">
                  Es aerolínea doméstica de México
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAdd}>
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Aerolínea</DialogTitle>
              <DialogDescription>
                Modifica los datos de la aerolínea
              </DialogDescription>
            </DialogHeader>
            {editingAirline && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Código IATA</Label>
                  <Input
                    value={editingAirline.code}
                    onChange={(e) => setEditingAirline({...editingAirline, code: e.target.value.toUpperCase()})}
                    maxLength={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nombre *</Label>
                  <Input
                    value={editingAirline.name}
                    onChange={(e) => setEditingAirline({...editingAirline, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Input
                    value={editingAirline.country}
                    onChange={(e) => setEditingAirline({...editingAirline, country: e.target.value})}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit_is_domestic"
                    checked={editingAirline.is_domestic_mexico}
                    onChange={(e) => setEditingAirline({...editingAirline, is_domestic_mexico: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="edit_is_domestic" className="cursor-pointer">
                    Es aerolínea doméstica de México
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEdit}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsAerolineas;
